<template><div ref="chart" class="statistics-extra-chart"></div></template>

<script>
const blue = '#409eff'
const colors = [blue, '#67c23a', '#e6a23c', '#909399', '#f56c6c']
const axisLabel = { color: '#909399', fontSize: 10 }
const splitLine = { lineStyle: { color: 'rgba(220, 223, 230, .65)' } }

function escapeHtml(value) {
  return String(value == null ? '' : value).replace(/[&<>"']/g, character => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[character]))
}

export default {
  name: 'StatisticsExtraChart',
  props: {
    kind: { type: String, required: true },
    data: { type: Array, default: () => [] }
  },
  watch: {
    data: { handler: 'updateChart', deep: true },
    kind: 'updateChart'
  },
  mounted() {
    this.chart = this.$echarts.init(this.$refs.chart)
    this.updateChart()
    if (typeof ResizeObserver !== 'undefined') {
      this.observer = new ResizeObserver(this.resizeChart)
      this.observer.observe(this.$refs.chart)
    } else window.addEventListener('resize', this.resizeChart)
  },
  beforeDestroy() {
    if (this.observer) this.observer.disconnect()
    else window.removeEventListener('resize', this.resizeChart)
    if (this.chart) this.chart.dispose()
  },
  methods: {
    resizeChart() {
      if (!this.chart) return
      this.chart.resize()
      const compact = this.$refs.chart.clientWidth < 420
      if (compact !== this.compact) this.updateChart()
    },
    updateChart() {
      if (!this.chart) return
      this.compact = this.$refs.chart.clientWidth < 420
      const builders = {
        months: this.monthOption,
        prices: this.priceOption,
        tags: this.tagOption,
        typeLanguages: this.typeLanguageOption,
        scoreSales: this.scoreSaleOption
      }
      this.chart.setOption(builders[this.kind].call(this), true)
    },
    monthOption() {
      const counts = new Map(this.data.map(item => [item.name, Number(item.value || 0)]))
      const today = new Date()
      const months = Array.from({ length: 24 }, (_, index) => {
        const date = new Date(today.getFullYear(), today.getMonth() - 23 + index, 1)
        return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
      })
      return {
        color: [blue],
        tooltip: { trigger: 'axis', axisPointer: { type: 'line' } },
        grid: { left: 14, right: 16, top: 24, bottom: 20, containLabel: true },
        xAxis: { type: 'category', boundaryGap: false, data: months, axisLabel: { ...axisLabel, interval: this.compact ? 5 : 2, formatter: value => value.slice(2) }, axisTick: { show: false } },
        yAxis: { type: 'value', minInterval: 1, axisLabel, splitLine },
        series: [{ name: '发售作品', type: 'line', smooth: .25, symbolSize: 5, lineStyle: { width: 3 }, areaStyle: { color: 'rgba(64, 158, 255, .15)' }, data: months.map(month => counts.get(month) || 0) }]
      }
    },
    priceOption() {
      const rows = this.data.slice().sort((a, b) => {
        const priceOf = item => item.name === '免费' ? 0 : item.name === '未知' ? Infinity : Number(item.name)
        return priceOf(a) - priceOf(b)
      })
      const labels = rows.map(item => item.name)
      const visible = this.compact ? 7 : 24
      const end = Math.min(100, 100 * visible / Math.max(labels.length, 1))
      const formatPrice = value => value === '免费' || value === '未知' ? value : `¥${Number(value).toLocaleString()}`
      return {
        color: [blue],
        tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' }, formatter: params => `${escapeHtml(formatPrice(params[0].axisValue))}<br/>${Number(params[0].value).toLocaleString()} 部作品` },
        grid: { left: 12, right: 16, top: 20, bottom: this.compact ? 62 : 50, containLabel: true },
        xAxis: { type: 'category', data: labels, axisLabel: { ...axisLabel, interval: 0, rotate: this.compact ? 35 : 0, formatter: formatPrice }, axisTick: { show: false } },
        yAxis: { type: 'value', minInterval: 1, axisLabel, splitLine },
        dataZoom: [{ type: 'slider', start: 0, end, bottom: 5, height: 16, brushSelect: false, borderColor: 'rgba(220, 223, 230, .8)', fillerColor: 'rgba(64, 158, 255, .17)', handleStyle: { color: blue } }],
        series: [{ name: '作品数', type: 'bar', barMaxWidth: 30, itemStyle: { borderRadius: [4, 4, 0, 0] }, data: rows.map(item => Number(item.value || 0)) }]
      }
    },
    tagOption() {
      const rows = this.data.slice(0, 15).reverse()
      return {
        color: [blue],
        tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' }, formatter: params => `${escapeHtml(params[0].axisValue)}<br/>${Number(params[0].value).toLocaleString()} 部作品` },
        grid: { left: 10, right: 24, top: 8, bottom: 18, containLabel: true },
        xAxis: { type: 'value', minInterval: 1, axisLabel, splitLine },
        yAxis: { type: 'category', data: rows.map(item => item.name), axisLabel: { ...axisLabel, width: this.compact ? 80 : 120, overflow: 'truncate' }, axisTick: { show: false } },
        series: [{ type: 'bar', barMaxWidth: 14, itemStyle: { borderRadius: [0, 4, 4, 0] }, data: rows.map(item => item.value) }]
      }
    },
    typeLanguageOption() {
      const totals = new Map()
      const languages = []
      this.data.forEach(item => {
        totals.set(item.type, (totals.get(item.type) || 0) + Number(item.count || 0))
        if (!languages.includes(item.language)) languages.push(item.language)
      })
      const types = Array.from(totals.keys()).sort((a, b) => totals.get(a) - totals.get(b))
      return {
        color: colors,
        tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
        legend: { top: 0, textStyle: { color: '#606266', fontSize: 10 }, itemWidth: 10, itemHeight: 10 },
        grid: { left: 10, right: 18, top: 35, bottom: 12, containLabel: true },
        xAxis: { type: 'value', minInterval: 1, axisLabel, splitLine },
        yAxis: { type: 'category', data: types, axisLabel, axisTick: { show: false } },
        series: languages.map(language => ({
          name: language, type: 'bar', stack: 'total', barMaxWidth: 16,
          data: types.map(type => {
            const row = this.data.find(item => item.type === type && item.language === language)
            return row ? row.count : 0
          })
        }))
      }
    },
    scoreSaleOption() {
      return {
        color: [blue],
        tooltip: {
          trigger: 'item',
          formatter: params => {
            const point = params.data
            return `${escapeHtml(point[3])}<br/>${escapeHtml(point[2])}<br/>评分 ${point[0]} · 销量 ${Number(point[1]).toLocaleString()}`
          }
        },
        grid: { left: 18, right: 28, top: 24, bottom: 30, containLabel: true },
        xAxis: { type: 'value', name: '评分', min: 0, max: 5, axisLabel, splitLine },
        yAxis: { type: 'log', name: '销量', min: 1, axisLabel: { ...axisLabel, formatter: value => Number(value).toLocaleString() }, splitLine },
        series: [{ name: '作品', type: 'scatter', symbolSize: 5, progressive: 1000, itemStyle: { opacity: .4 }, data: this.data.map(item => [Number(item.score), Number(item.saleCount), item.gameId, item.name]) }]
      }
    }
  }
}
</script>

<style scoped>.statistics-extra-chart { width: 100%; height: 100%; min-height: 0; }</style>
